import cn from 'classnames'
import Text from 'shared/components/typography/Text'
import { Button, Flex, UploadFile } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import { FormattedMessage } from 'react-intl'
import { useMemo, useId } from 'react'
import { ImageControls, SelectType } from '../ChooseImageModal/types'
import { checkImageAsset } from './utils'
import { lang } from './lang'
import ImageContent from './ImageContent'
import s from './s.module.css'
import { ImageProp } from './types'
import { ImageAsset } from '@/types/assets'

interface Props<T extends ImageProp> {
  imagesList: T[]
  muteNotSelected?: boolean
  columnCount: number
  containerId?: string
  selectedImages?: string[] | null
  controls?: ImageControls<T>
  selectType?: SelectType
  processingImages?: string[]
  sizes?: string
}

const DUMMY_IMAGE_ID = 'dummy-upload'

const DUMMY_IMAGE: ImageAsset = {
  id: DUMMY_IMAGE_ID,
  url: '',
  srcSet: '',
  preview_url: '',
  is_default: false,
}

function Masonry<T extends ImageProp>({ imagesList, columnCount, controls, containerId, ...rest }: Props<T>) {
  const id = useId()
  const withUpload = controls?.onUpload != null
  const imagesListSrc = useMemo(
    () => (withUpload ? [DUMMY_IMAGE as T, ...imagesList] : imagesList),
    [imagesList, withUpload]
  )
  const isImageAsset = checkImageAsset(imagesList[0])

  const list = useMemo(() => {
    let list: T[][] = []
    const rowsCount = Math.ceil(imagesListSrc.length / columnCount)

    if (rowsCount === 0) {
      list = [[...imagesListSrc]]
    } else {
      for (let i = 0; i < columnCount; i += 1) {
        const temp: T[] = []
        for (let k = 0; k < rowsCount; k += 1) {
          const item = imagesListSrc[i + k * columnCount]
          if (item) {
            temp.push(item)
          }
        }
        list.push(temp)
      }
    }

    return list
  }, [columnCount, imagesListSrc])

  return (
    <div className={cn(s.grid, { [s.borderGrid]: rest.selectType === 'border' })} style={{ columnCount }} id={id}>
      {list.map((column, columnIndex) => {
        return (
          <div key={`column-${columnIndex}`}>
            {column.map(item => {
              if ((item as ImageAsset).id === DUMMY_IMAGE_ID) {
                return (
                  <div className={s.addButtonWrap} key={DUMMY_IMAGE_ID}>
                    <Button color="default" variant="dashed" className={s.addButton} onClick={controls?.onUpload}>
                      <Flex vertical align="center" justify="center" gap={4}>
                        <PlusOutlined style={{ fontSize: '28px' }} />
                        <Text fontType="description">
                          <FormattedMessage {...lang.add} />
                        </Text>
                      </Flex>
                    </Button>
                  </div>
                )
              }

              const assetId = isImageAsset ? (item as ImageAsset).id : (item as UploadFile).uid

              return (
                <ImageContent<T>
                  key={assetId}
                  img={item}
                  muteNotSelected={rest.muteNotSelected}
                  isSelected={rest.selectedImages?.some(selected => selected === assetId)}
                  id={containerId}
                  controls={controls}
                  selectType={rest.selectType}
                  isProcessing={rest.processingImages?.some(processing => processing === assetId)}
                  sizes={rest.sizes}
                />
              )
            })}
          </div>
        )
      })}
    </div>
  )
}

export default Masonry
